import type { TrackingFieldName } from './trackingPixelsForm';

export interface ProviderFieldSpec {
  name: TrackingFieldName;
  label: string;
  placeholder: string;
  optional?: boolean;
}

export interface ProviderSpec {
  key: 'meta' | 'reddit' | 'google_ads' | 'ga4';
  title: string;
  hint: string;
  /** What the partner does in their own ad account; gets the game's domain. */
  steps: (iframeHost: string) => string[];
  fields: ProviderFieldSpec[];
}

const OPTIMISE_FOR_PURCHASE = 'For campaigns, optimise for Purchase — it carries the order amount and currency.';

export const PROVIDERS: ProviderSpec[] = [
  {
    key: 'meta',
    title: 'Meta Pixel',
    hint: 'Events Manager → Data sources → your pixel → Settings: the numeric Pixel ID (shown as "Dataset ID" in newer accounts).',
    steps: (iframeHost) => [
      'Copy the Pixel ID (Dataset ID) from Events Manager → Data sources → your pixel → Settings.',
      `If Traffic permissions are on for this pixel (Settings → Traffic permissions → allow list), add ${iframeHost} — otherwise Meta drops the game's events.`,
      'Nothing else is needed: PageView, ViewContent, InitiateCheckout and Purchase are standard events and appear in Events Manager on their own.',
      OPTIMISE_FOR_PURCHASE,
    ],
    fields: [{ name: 'metaPixelId', label: 'Meta pixel ID', placeholder: '1234567890123456' }],
  },
  {
    key: 'reddit',
    title: 'Reddit Pixel',
    hint: 'Reddit Ads → Events Manager → your pixel: the Pixel ID (starts with a2_ or t2_).',
    steps: () => [
      'Copy the Pixel ID from Reddit Ads → Events Manager → Reddit Pixel.',
      'Nothing else is needed: PageVisit, ViewContent, AddToCart and Purchase are standard events.',
      OPTIMISE_FOR_PURCHASE,
    ],
    fields: [{ name: 'redditPixelId', label: 'Reddit pixel ID', placeholder: 'a2_abc123' }],
  },
  {
    key: 'google_ads',
    title: 'Google Ads',
    hint: 'Paste the Conversion ID and label from Tag setup → "Use Google Tag Manager". The whole "AW-123456789/AbC-dEf_123" value from an event snippet works too — paste it into Conversion ID.',
    steps: () => [
      'Goals → Conversions → Summary → Create conversion action → Conversions on a website → add a conversion action manually.',
      'Category: Purchase. Value: "Use different values for each conversion". Count: "Every".',
      'Save, open Tag setup → "Use Google Tag Manager" and copy the Conversion ID and the Conversion label into the fields below.',
      'Optional: a second conversion action with category "Begin checkout" — its label goes into Checkout conversion label.',
    ],
    fields: [
      { name: 'googleAdsConversionId', label: 'Google Ads conversion ID', placeholder: 'AW-123456789' },
      { name: 'googleAdsPurchaseLabel', label: 'Purchase conversion label', placeholder: 'AbC-dEf_123' },
      { name: 'googleAdsCheckoutLabel', label: 'Checkout conversion label', placeholder: 'XyZ-987_abc', optional: true },
    ],
  },
  {
    key: 'ga4',
    title: 'Google Analytics 4',
    hint: 'Admin → Data streams → your web stream: the Measurement ID (starts with G-).',
    steps: () => [
      'Copy the Measurement ID from Admin → Data streams → your web stream.',
      'In the same stream open Enhanced measurement → Page views → Show advanced settings and turn off "Page changes based on browser history events" — otherwise every screen inside the game counts as a page view.',
      'purchase is a key event by default; view_item and begin_checkout arrive as standard e-commerce events.',
      'Players show up in their own sessions inside the game: GA4 cannot join them to their visit to your site.',
    ],
    fields: [{ name: 'ga4MeasurementId', label: 'GA4 measurement ID', placeholder: 'G-ABC123XYZ' }],
  },
];

export const REPORT_CURRENCY_FIELD: ProviderFieldSpec = {
  name: 'reportCurrency',
  label: 'Report currency',
  placeholder: 'USD',
};
